import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { getNestedDotNotationKeys } from 'utils/v2';

/**
 * Custom hook to handle form field validation using Formik and Yup schema.
 *
 * @param {Object} params - Parameters for the hook.
 * @param {Object} params.formik - Formik instance.
 * @param {Object} params.schema - Yup validation schema.
 * @param {Array<string>} [params.requiredFields] - List of dot notated required fields.
 * @param {Array<Array<string>>} [params.fieldGroups] - Groups of fields that are validated together.
 *
 * @returns {Object} - Returns an object containing field error messages and validation states.
 * @returns {Object} fieldErrorMsg - Object containing error messages for each field.
 * @returns {Object} fieldValidation - Object containing validation states for each field.
 * @returns {Function} setUpdatedField - Function to get the last updated field key.
 * @returns {Function} resetFormFieldValidator - Function to reset the hook states.
 */
const useFormFieldValidator = ({
  formik,
  schema,
  requiredFields,
  fieldGroups,
}) => {
  const { values, touched, errors } = formik;
  const [fieldErrorMsg, setFieldErrorMsg] = useState({});
  const [fieldValidation, setFieldValidation] = useState({});
  const [updatedField, setUpdatedField] = useState('');

  const checkValueEmpty = useCallback(
    (value) =>
      (!value && value !== 0) ||
      (typeof value === 'object' && _.isEmpty(value)),
    [],
  );

  const isFieldRequired = useCallback(
    (field) => {
      if (requiredFields) {
        const isRequired = requiredFields.some(
          (fieldName) => fieldName === field,
        );
        if (isRequired) return true;
      }
      let tempSchema = schema;
      // eslint-disable-next-line consistent-return
      field.split('.').forEach((element) => {
        const key = element.split('[')[0];
        tempSchema = tempSchema?.fields?.[key];
        if (!tempSchema) return false;
      });
      return tempSchema?.exclusiveTests?.required || false;
    },
    [requiredFields],
  );

  const getValidationAndErrorMsg = useCallback(
    (field) => {
      const isRequired = isFieldRequired(field);
      const value = _.get(values, field);
      const isValueEmpty = checkValueEmpty(value);

      const valid =
        isRequired || !isValueEmpty
          ? _.get(errors, field) === undefined
          : undefined;
      const errorMsg = (isRequired || !isValueEmpty) && _.get(errors, field);
      return { valid, errorMsg };
    },
    [isFieldRequired, values, errors],
  );

  const updateFields = useCallback(
    (fields) => {
      const validStates = {};
      const errorMsgStates = {};
      fields.forEach((currentField) => {
        if (
          _.has(touched, currentField) ||
          (_.has(errors, currentField) &&
            !checkValueEmpty(_.get(values, currentField)))
        ) {
          const { valid, errorMsg } = getValidationAndErrorMsg(currentField);
          validStates[currentField] = valid;
          errorMsgStates[currentField] = errorMsg;
        } else {
          validStates[currentField] = undefined;
        }
      });
      setFieldValidation((prevStates) => ({
        ..._.cloneDeep(prevStates),
        ...validStates,
      }));
      setFieldErrorMsg((prevStates) => ({
        ..._.cloneDeep(prevStates),
        ...errorMsgStates,
      }));
    },
    [getValidationAndErrorMsg, touched, errors, values],
  );

  const resetFormFieldValidator = useCallback(() => {
    setFieldValidation({});
    setFieldErrorMsg({});
    setUpdatedField(null);
  }, []);

  useEffect(() => {
    if (!updatedField) {
      setFieldErrorMsg({});
      setFieldValidation({});

      const initialFieldValidation = getNestedDotNotationKeys(errors)?.filter(
        (key) => !checkValueEmpty(_.get(values, key)),
      );

      updateFields(initialFieldValidation);
    } else {
      const relatedGroup = fieldGroups?.find((group) =>
        group.some((field) => field === updatedField),
      );
      const fieldsToUpdate = relatedGroup ?? [updatedField];

      updateFields(fieldsToUpdate);
    }
  }, [updatedField, updateFields, errors, values]);

  return {
    fieldErrorMsg,
    fieldValidation,
    resetFormFieldValidator,
    setUpdatedField,
  };
};

export default useFormFieldValidator;
